import { useParams, type RouteObject } from 'react-router';
import type { ReactElement } from 'react';
import type { ContainerKind } from '../../shared/actions/model/action-row';
import { READABLE_OFFLINE } from '../../core/pwa/works-offline';

/**
 * Two mounts, one lazy chunk: a list per kind and the one detail page they share.
 *
 * **`kind` is a closure literal, not route data, and `id` is read from the params right here.** The
 * segment that reaches this file already says which kind it is — `/projects` or `/contexts` — so it
 * hands that fact to the page directly instead of the page re-deriving it from a string it would
 * have to parse. Angular expressed the same idea as `data: { kind }` plus
 * `withComponentInputBinding()`; React needs neither, because a closure over the literal is already
 * type-checked against `ContainerKind` and a typo cannot compile.
 *
 * That configuration was the reason `containers.routes.spec.ts` navigated for real rather than
 * asserting structurally: dropping `withComponentInputBinding()` was a one-line edit with no type
 * error, and the page would then render with unset required inputs and never load. **The React
 * version cannot fail that way** — `id` comes from `useParams()` inside the element itself — so the
 * spec's remaining job is narrower and it says so: that each path mounts the right page, and that
 * the detail mount asks the API for the right KIND of container.
 *
 * The wrapper lives inside the `lazy` factory so that importing this route table does not pull
 * `container-page` (and with it both settings panels, the action row and the quick-add box) into the
 * chunk a practitioner downloads before they have opened a container.
 *
 * Two exported arrays rather than one nested tree because the two mounts are siblings under the
 * guarded shell, not parent and child: `/contexts` is not under `/projects`, and a `''`-path detail
 * route cannot serve both.
 */
const detail = (kind: ContainerKind): RouteObject => ({
  path: ':id',
  // 036 (issue 221, FR-021): a single context or project is readable offline — the page shows a
  // stored view rather than falling back to 021's offline panel.
  handle: { [READABLE_OFFLINE]: true },
  lazy: async () => {
    const { default: ContainerPage } = await import('./container-page');
    function ContainerDetail(): ReactElement {
      const { id } = useParams();
      return <ContainerPage kind={kind} id={id ?? ''} />;
    }
    return { Component: ContainerDetail };
  },
});

const list = (load: () => Promise<{ default: React.ComponentType }>): RouteObject => ({
  index: true,
  // 036 (issue 221, FR-021): both the contexts list and the projects list are readable offline.
  handle: { [READABLE_OFFLINE]: true },
  lazy: async () => ({ Component: (await load()).default }),
});

export const projectRoutes: RouteObject[] = [
  list(() => import('./projects-page')),
  detail('project'),
];

export const contextRoutes: RouteObject[] = [
  list(() => import('./contexts-page')),
  detail('context'),
];
